import React, { useState, useEffect, useRef, useMemo } from "react";
import "../Events/EventPages/NetworkMultiplier/multiplier.css";
import "./event.css";
import BookSlot from "../../Modal/BookSlot/BookSlot";
import { Link, useParams, useLocation } from "react-router-dom";
import { eventAPI } from "../../../services/eventAPI";

import { BACKEND_URL } from '../../../config';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  const clean = url.startsWith("/") ? url : `/${url}`;
  return `${BACKEND_URL}${clean}`;
};

const normalizeEventLocation = (event) => {
  return event.city || event.location || "Unknown City";
};

const formatEventDate = (dateStr, format = "full") => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (format === "month-year") {
    return date.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  }
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

// ─── Description Modal ────────────────────────────────────────────────────────
const DescModal = ({ event, onClose, onViewGallery }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  if (!event) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16,
          maxWidth: 680, width: "100%",
          maxHeight: "85vh", display: "flex", flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
          overflow: "hidden",
        }}
      >
        <div style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid #f1f1f1",
          display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", gap: 12,
          flexShrink: 0,
        }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#861b68",
              textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4,
            }}>
              {normalizeEventLocation(event)} · {formatEventDate(event.date, event.dateDisplayFormat)}
            </div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1f2937", lineHeight: 1.3 }}>
              {event.title}
            </h3>
            {event.location && (
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                📍 {event.location}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#f3f4f6", border: "none", borderRadius: "50%",
              width: 32, height: 32, fontSize: 20, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#374151", flexShrink: 0, lineHeight: 1,
            }}
          >×</button>
        </div>
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.9, margin: 0, whiteSpace: "pre-wrap" }}>
            {event.description}
          </p>
        </div>
        <div style={{
          padding: "14px 24px", borderTop: "1px solid #f1f1f1",
          display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0,
        }}>
          {Array.isArray(event.gallery) && event.gallery.length > 0 && (
            <button
              onClick={() => { onClose(); if (onViewGallery) onViewGallery(event); }}
              style={{
                background: "transparent", color: "#861b68",
                border: "2px solid #861b68", borderRadius: 8,
                padding: "9px 18px", fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}
            >
              View Gallery
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: "#861b68", color: "#fff", border: "none",
              borderRadius: 8, padding: "9px 22px", fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Gallery Modal ────────────────────────────────────────────────────────────
const GalleryModal = ({ event, onClose, initialIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copyMsg, setCopyMsg] = useState("");
  const images = event?.gallery || [];

  useEffect(() => { setActiveIndex(initialIndex); }, [initialIndex]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (isFullscreen) { setIsFullscreen(false); } else { onClose(); }
      }
      if (e.key === "ArrowRight") setActiveIndex((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setActiveIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [images.length, onClose, isFullscreen]);

  const shareImage = async () => {
    const currentImage = images[activeIndex];
    try {
      if (currentImage.startsWith("data:") && navigator.share) {
        const [meta, base64] = currentImage.split(",");
        const mimeType = meta.split(":")[1].split(";")[0];
        const byteChars = atob(base64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: mimeType });
        const file = new File([blob], `${event.title}_${activeIndex + 1}.jpg`, { type: mimeType });
        await navigator.share({ title: event.title, files: [file] });
      } else if (navigator.share) {
        await navigator.share({ title: event.title, url: currentImage });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setCopyMsg("Page link copied!");
        setTimeout(() => setCopyMsg(""), 2000);
      }
    } catch {
      setCopyMsg("Could not share");
      setTimeout(() => setCopyMsg(""), 2000);
    }
  };

  const downloadImage = async () => {
    const currentImage = images[activeIndex];
    try {
      if (currentImage.startsWith("data:")) {
        const [meta, base64] = currentImage.split(",");
        const mimeType = meta.split(":")[1].split(";")[0];
        const byteChars = atob(base64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${event.title.replace(/\s+/g, "_")}_${activeIndex + 1}.jpg`;
        document.body.appendChild(link); link.click();
        document.body.removeChild(link); URL.revokeObjectURL(url);
      } else {
        const response = await fetch(currentImage);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${event.title.replace(/\s+/g, "_")}_${activeIndex + 1}.jpg`;
        document.body.appendChild(link); link.click();
        document.body.removeChild(link); URL.revokeObjectURL(url);
      }
    } catch {
      window.open(currentImage, "_blank");
    }
  };

  if (!event || !images.length) return null;

  const actionBtnStyle = {
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: 10, padding: "8px 14px",
    color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", gap: 6,
    fontSize: 13, fontWeight: 600,
    transition: "background 0.2s",
    backdropFilter: "blur(4px)",
  };

  const arrowBtnStyle = {
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "50%", width: 52, height: 52,
    color: "#fff", fontSize: "1.8rem", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, transition: "background 0.2s",
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)",
        zIndex: 99999, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute", top: 0, left: 0, right: 0,
          padding: "16px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)",
          zIndex: 10,
        }}
      >
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{event.title}</div>
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>
            {activeIndex + 1} / {images.length}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {copyMsg && (
            <span style={{
              background: "#861b68", color: "#fff", borderRadius: 6,
              padding: "4px 10px", fontSize: 12, fontWeight: 600,
            }}>{copyMsg}</span>
          )}
          <button style={actionBtnStyle} onClick={shareImage}>📤 Share</button>
          <button style={actionBtnStyle} onClick={downloadImage}>⬇️ Download</button>
          <button style={actionBtnStyle} onClick={() => setIsFullscreen((f) => !f)}>
            {isFullscreen ? "⊡ Exit" : "⛶ Fullscreen"}
          </button>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "50%", width: 36, height: 36,
              color: "#fff", fontSize: 20, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
            }}
          >×</button>
        </div>
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex", alignItems: "center", gap: 16,
          width: "100%", justifyContent: "center",
          padding: "80px 20px 20px", flex: 1,
        }}
      >
        {images.length > 1 && (
          <button onClick={() => setActiveIndex((i) => (i - 1 + images.length) % images.length)} style={arrowBtnStyle}>‹</button>
        )}
        <img
          src={images[activeIndex]}
          alt={`${event.title} - ${activeIndex + 1}`}
          onError={(e) => { e.target.style.display = "none"; }}
          onClick={() => images.length > 1 && setActiveIndex((i) => (i + 1) % images.length)}
          style={{
            maxWidth: isFullscreen ? "92vw" : "65vw",
            maxHeight: isFullscreen ? "82vh" : "60vh",
            borderRadius: 12, objectFit: "contain", display: "block",
            boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
            cursor: images.length > 1 ? "pointer" : "default",
            transition: "all 0.3s ease",
          }}
        />
        {images.length > 1 && (
          <button onClick={() => setActiveIndex((i) => (i + 1) % images.length)} style={arrowBtnStyle}>›</button>
        )}
      </div>

      {images.length > 1 && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex", gap: 8, flexWrap: "wrap",
            justifyContent: "center", maxWidth: "80vw", padding: "0 20px 20px",
          }}
        >
          {images.map((img, i) => (
            <img
              key={i} src={img} alt={`thumb-${i}`}
              onClick={() => setActiveIndex(i)}
              style={{
                width: 56, height: 56, objectFit: "cover", borderRadius: 6, cursor: "pointer",
                border: i === activeIndex ? "2px solid #861b68" : "2px solid rgba(255,255,255,0.2)",
                opacity: i === activeIndex ? 1 : 0.5,
                transition: "all 0.2s ease",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Image Zoom Modal ─────────────────────────────────────────────────────────
const ImageZoomModal = ({ src, alt, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}
    >
      <img
        src={src} alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "90vw", maxHeight: "88vh", borderRadius: 12, objectFit: "contain", boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}
      />
      <button
        onClick={onClose}
        style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 36, height: 36, color: "#fff", fontSize: 20, cursor: "pointer" }}
      >×</button>
    </div>
  );
};

// ─── Past Event Card ──────────────────────────────────────────────────────────
const PastEventCard = ({ event, onDescClick, onGalleryClick, onVideoClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);
  const hasGallery = Array.isArray(event.gallery) && event.gallery.length > 0;

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setShowTooltip(true), 2000);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    setShowTooltip(false);
  };

  return (
    <div className="past-event-card-new">
      <div
        className="pec-image-wrap"
        onClick={(e) => { e.stopPropagation(); hasGallery && onGalleryClick(event, 0); }}
        style={{ cursor: hasGallery ? "pointer" : "default" }}
      >
        <img
          src={event.imageSrc || getImageUrl(event.imageUrl)}
          alt={event.title}
          loading="lazy"
          decoding="async"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        {hasGallery && (
          <>
            <div className="pec-img-overlay" />
            <span className="pec-gallery-badge">🖼 {event.gallery.length} photos · Click to view</span>
          </>
        )}
        {hasGallery && (
          <div className="pec-img-actions" onClick={(e) => e.stopPropagation()}>
            <button
              className="pec-action-btn"
              title="Share"
              onClick={async () => {
                if (navigator.share) {
                  try { await navigator.share({ title: event.title, url: window.location.href }); } catch {}
                } else {
                  try { await navigator.clipboard.writeText(window.location.href); alert("Copied!"); } catch {}
                }
              }}
            >📤</button>
            <button className="pec-action-btn" title="Open gallery fullscreen" onClick={() => onGalleryClick(event, 0)}>⛶</button>
          </div>
        )}
      </div>

      <div className="pec-body">
        <div className="pec-date-badge">{formatEventDate(event.date, event.dateDisplayFormat)}</div>
        <h3 className="pec-title">{event.title}</h3>
        <a
          href={event.locationlink}
          className="pec-location"
          target="_blank"
          rel="noopener noreferrer"
        >
          📍 {event.location}
          {event.city && event.city !== event.location ? `, ${event.city}` : ""}
        </a>
        <p
          className="pec-desc"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => onDescClick(event)}
          style={{ cursor: "pointer", position: "relative" }}
        >
          {event.description}
          {showTooltip && (
            <span style={{
              position: "absolute", bottom: 0, right: 0,
              background: "rgba(134, 27, 104, 0.9)", color: "white",
              padding: "4px 8px", borderRadius: "4px", fontSize: "12px",
              fontWeight: "bold", pointerEvents: "none", whiteSpace: "nowrap",
              zIndex: 10, animation: "fadeIn 0.2s ease-in-out",
            }}>
              Click for read more
            </span>
          )}
        </p>
      </div>

      <div className="pec-footer">
        <button
          className="pec-btn"
          disabled={!hasGallery}
          style={!hasGallery ? { opacity: 0.55, cursor: "not-allowed" } : {}}
          onClick={(e) => { e.stopPropagation(); if (hasGallery) onGalleryClick(event, 0); }}
        >
          View Gallery
        </button>
        <button
          className="pec-btn"
          disabled={!event.videos?.length}
          style={!event.videos?.length ? { opacity: 0.55, cursor: "not-allowed" } : {}}
          onClick={() => onVideoClick && onVideoClick(event)}
        >
          {event.videos?.length ? " View Videos" : "View Videos"}
        </button>
      </div>
    </div>
  );
};

// ─── Upcoming Event Card ──────────────────────────────────────────────────────
const UpcomingEventCard = ({ event, onTabSwitch, onRsvpClick }) => {
  const [zoomSrc, setZoomSrc] = useState(null);
  const imgSrc = event.imageSrc || getImageUrl(event.imageUrl);

  return (
    <div className="network-event-card-premium">
      <div className="card-inner">


        <div
          className="card-image-wrapper"
          style={{ cursor: imgSrc ? 'zoom-in' : 'default' }}
          onClick={() => imgSrc && setZoomSrc(imgSrc)}
        >
          {imgSrc && (
            <img
              src={imgSrc}
              alt={event.title}
              className="city-image"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="image-overlay">
            <span className="event-date-badge">
              {formatEventDate(event.date, event.dateDisplayFormat)}
            </span>
          </div>
      
        </div>

        <div className="card-content">
          {event.locationlink ? (
            <a href={event.locationlink} className="location-tag" target="_blank"
              rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              📍 {event.location}
            </a>
          ) : (
            <span className="location-tag">📍 {event.location}</span>
          )}
          <h3 className="event-title">{event.title}</h3>
          <p className="event-desc">{event.description}</p>
          <div className="card-footer dual-action">
            <button
              className="btn-action-primary left-btn"
              style={{ border: "none", cursor: "pointer", textAlign: "center" }}
              onClick={() => onRsvpClick && onRsvpClick(event)}
            >
              RSVP
            </button>
            <button
              className="btn-action-outline right-btn"
              onClick={() => onTabSwitch(event.city || event.location)}
            >
              Past Event
            </button>
          </div>
        </div>
      </div>

      {zoomSrc && (
        <ImageZoomModal src={zoomSrc} alt={event.title} onClose={() => setZoomSrc(null)} />
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Events = () => {
  const location = useLocation();
  const { city } = useParams();
  document.title = city ? `CITY=${city}` : "IHN Events";

  const [videoState, setVideoState] = useState(null);
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const cityRefs = useRef({});
  const [activeTab, setActiveTab] = useState("upcoming");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedCity, setSelectedCity] = useState("All");
  const [galleryState, setGalleryState] = useState(null);
  const [descEvent, setDescEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    const loadEvents = async () => {
      setLoadingEvents(true);
      try {
        const [upcomingResponse, pastResponse] = await Promise.all([
          eventAPI.getAll({ category: "upcoming", status: "active" }),
          eventAPI.getAll({ category: "past", status: "active" }),
        ]);
        if (upcomingResponse.success) setUpcomingEvents(upcomingResponse.data.sort((a, b) => new Date(a.date) - new Date(b.date)));
        if (pastResponse.success) setPastEvents(pastResponse.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } catch (error) {
        console.error("Failed to load events:", error);
      } finally {
        setLoadingEvents(false);
      }
    };
    loadEvents();
  }, []);

useEffect(() => {
  if (!city || upcomingEvents.length === 0) return;

  const slugify = (value = "") =>
    value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

const matchedEvent = upcomingEvents.find(
  (event) => slugify(event.city) === slugify(city)
);

console.log("URL city:", city);
console.log("Matched event:", matchedEvent);
console.log("All cities:", upcomingEvents.map(e => e.city));

if (matchedEvent) {
  setSelectedEvent(matchedEvent);
  setIsBookModalOpen(true);
  setActiveTab("upcoming");
  setSelectedCity(matchedEvent.city || matchedEvent.location);
}
}, [city, location.key, upcomingEvents]);
  useEffect(() => {
    const handleResize = () => {};
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const allEvents = useMemo(
    () => activeTab === "upcoming" ? upcomingEvents : pastEvents,
    [activeTab, upcomingEvents, pastEvents]
  );

  // City list from ALL events (both tabs) so tabs persist on switch
const cityList = useMemo(() => {
  const source = activeTab === "upcoming" ? upcomingEvents : pastEvents;
  const cities = source.map((e) => e.city || e.location).filter(Boolean);
  return ["All", ...Array.from(new Set(cities))];
}, [activeTab, upcomingEvents, pastEvents]);


  const scrollCityTabs = (direction) => {
    const track = document.getElementById("cityTrack");
    if (!track) return;
    const tabs = Array.from(track.children);
    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let closestIndex = 0, minDiff = Infinity;
    tabs.forEach((tab) => {
      const diff = Math.abs(tab.getBoundingClientRect().left + tab.getBoundingClientRect().width / 2 - trackCenter);
      if (diff < minDiff) { minDiff = diff; closestIndex = tabs.indexOf(tab); }
    });
    const nextIndex = direction === "right"
      ? Math.min(closestIndex + 1, tabs.length - 1)
      : Math.max(closestIndex - 1, 0);
    const target = tabs[nextIndex].getBoundingClientRect();
    track.scrollBy({ left: target.left + target.width / 2 - trackCenter, behavior: "smooth" });
  };

  const filteredEvents = useMemo(() => allEvents.filter((event) => {
    const eventDate = new Date(event.date);
    const matchesSearch =
      (event.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.location || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.city || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFromDate = fromDate ? eventDate >= new Date(fromDate) : true;
    const matchesToDate = toDate ? eventDate <= new Date(toDate) : true;
    const eventCity = event.city || event.location;
    const matchesCity = selectedCity === "All" || eventCity === selectedCity;

     console.log({ 
      selectedCity,
      eventCity,
      event: event.title,
      matchesCity,
      all: {
       matchesSearch,
       matchesFromDate,
       matchesToDate,
       matchesCity,
      },
    });

return matchesSearch && matchesFromDate && matchesToDate && matchesCity;
  }), [allEvents, searchTerm, fromDate, toDate, selectedCity]);

  useEffect(() => {
    if (!selectedCity || selectedCity === "All") return;
    const activeCityEl = cityRefs.current[selectedCity];
    const track = document.getElementById("cityTrack");
    if (activeCityEl && track) {
      const trackRect = track.getBoundingClientRect();
      const cityRect = activeCityEl.getBoundingClientRect();
      track.scrollBy({
        left: cityRect.left - trackRect.left - trackRect.width / 2 + cityRect.width / 2,
        behavior: "smooth",
      });
    }
  }, [selectedCity, activeTab]);

  const openGallery = (event, imageIndex = 0) => setGalleryState({ event, imageIndex });

  return (
    <div className="page-content">
      <div className="network-container">

        <section className="about-network">
          <div className="container">
            <h1 className="text-center heading">IHN Events</h1>
            <p style={{ textAlign: "justify" }} className="mx-5">
              Step into an experience designed to inspire, connect, and create opportunities. Our event brings together industry leaders, innovators, and decision-makers from across the globe to share insights, explore trends, and build meaningful collaborations. Whether you're here to learn, network, or showcase your brand, this platform is where ideas turn into impact and conversations turn into growth.
            </p>
          </div>
        </section>

        <section className="filter-section">
          <input
            type="text"
            placeholder="Search by event name, city or keyword"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="date-filters">
            <label>From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <label>To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </section>

        <div className="network-tabs-section mb-5 text-center">
          <button
            className={`tab-btn ${activeTab === "upcoming" ? "active" : ""}`}
            onClick={() => { setSelectedCity("All"); setActiveTab("upcoming"); }}
          >
            Upcoming
          </button>
          <button
            className={`tab-btn ${activeTab === "past" ? "active" : ""}`}
            onClick={() => { setActiveTab("past"); setSelectedCity("All"); }}
          >
            Past
          </button>
        </div>

        <div className="container">
          <div className="city-carousel-wrapper">
            <button className="city-nav left" onClick={() => scrollCityTabs("left")}>‹</button>
            <div className="city-tabs" id="cityTrack">
              {cityList.map((c) => (
                <button
                  key={c}
                  ref={(el) => (cityRefs.current[c] = el)}
                  className={`tab-btn ${selectedCity === c ? "active" : ""}`}
                  onClick={() => setSelectedCity(c)}
                >
                  {c}
                </button>
              ))}
            </div>
            <button className="city-nav right" onClick={() => scrollCityTabs("right")}>›</button>
          </div>
        </div>

        {loadingEvents ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{
              width: 48, height: 48, border: "4px solid #861b68",
              borderTopColor: "transparent", borderRadius: "50%",
              animation: "spin 0.8s linear infinite", margin: "0 auto 16px",
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: "#6b7280" }}>Loading events…</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <p className="no-results">No events found.</p>
        ) : (
          <>
{activeTab === "upcoming" && (
  <div className="events-grid">
    {filteredEvents.map((event) => (
      <UpcomingEventCard
        key={event.id}
        event={event}
        onTabSwitch={(c) => { setActiveTab("past"); setSelectedCity(c); }}
        onRsvpClick={(ev) => {
          setSelectedEvent(ev);
          setIsBookModalOpen(true);
        }}
      />
    ))}
  </div>
)}
            {activeTab === "past" && (
              <div className="past-events-grid">
                {filteredEvents.map((event) => (
                  <PastEventCard
                    key={event.id}
                    event={event}
                    onDescClick={(ev) => setDescEvent(ev)}
                    onGalleryClick={(ev, idx) => openGallery(ev, idx)}
                    onVideoClick={(ev) => setVideoState({ event: ev })}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Gallery Modal */}
      {galleryState && (
        <GalleryModal
          key={galleryState.event.id}
          event={galleryState.event}
          initialIndex={galleryState.imageIndex}
          onClose={() => setGalleryState(null)}
        />
      )}

      {/* Video Modal */}
      {videoState && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 99999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setVideoState(null)}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 800 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{videoState.event.title} — Videos</span>
              <button onClick={() => setVideoState(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 36, height: 36, color: "#fff", fontSize: 20, cursor: "pointer" }}>×</button>
            </div>
            {videoState.event.videos.map((vid, i) => (
              <div key={i} style={{ marginBottom: 16, borderRadius: 12, overflow: "hidden", background: "#000" }}>
                {vid.isYoutube || vid.mimeType === "video/youtube" ? (
                  <iframe
                    src={vid.src}
                    style={{ width: "100%", height: 420, border: "none", display: "block" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`video-${i}`}
                  />
                ) : (
                  <video src={vid.src} controls style={{ width: "100%", maxHeight: 420, display: "block" }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Description Modal */}
      {descEvent && (
        <DescModal
          event={descEvent}
          onClose={() => setDescEvent(null)}
          onViewGallery={(ev) => openGallery(ev, 0)}
        />
      )}

      <BookSlot
        isOpen={isBookModalOpen}
        onClose={() => setIsBookModalOpen(false)}
        eventData={selectedEvent}
      />
    </div>
  );
};

export default Events;
